/**
 * Config Command
 * View and manage CLI configuration
 */

import { Command } from 'commander';
import {
    getConfig,
    getConfigPath,
    setConfigValue,
    resetConfig,
    type PaeanConfig,
} from '../utils/config.js';
import * as output from '../utils/output.js';

/** Keys that contain sensitive values and should be redacted in display */
const SENSITIVE_KEYS = new Set(['token', 'refreshToken']);

/** Keys that users are allowed to set directly */
const SETTABLE_KEYS = new Set([
    'apiUrl', 'webUrl', 'defaultPriority', 'outputFormat',
]);

export const configCommand = new Command('config')
    .description('View and manage CLI configuration')
    .action(() => {
        showConfig();
    });

// Subcommand: config show
configCommand
    .command('show')
    .description('Display current configuration')
    .option('--json', 'Output as JSON')
    .option('--all', 'Show all values including sensitive tokens')
    .action((options) => {
        showConfig(options.json, options.all);
    });

// Subcommand: config path
configCommand
    .command('path')
    .description('Show configuration file path')
    .action(() => {
        console.log(getConfigPath());
    });

// Subcommand: config set
configCommand
    .command('set <key> <value>')
    .description('Set a configuration value')
    .action((key: string, value: string) => {
        if (!SETTABLE_KEYS.has(key)) {
            output.error(`Cannot set "${key}". Settable keys: ${Array.from(SETTABLE_KEYS).join(', ')}`);
            process.exit(1);
        }

        setConfigValue(key as keyof PaeanConfig, value as never);
        output.success(`Set ${key} = ${value}`);
    });

// Subcommand: config reset
configCommand
    .command('reset')
    .description('Reset configuration to defaults (clears auth)')
    .option('--force', 'Skip confirmation')
    .action(async (options) => {
        if (!options.force) {
            const readline = await import('readline');
            const rl = readline.createInterface({
                input: process.stdin,
                output: process.stdout,
            });
            const answer = await new Promise<string>((resolve) => {
                rl.question('This will clear all config including auth. Continue? (y/n): ', resolve);
            });
            rl.close();

            if (answer.toLowerCase() !== 'y' && answer.toLowerCase() !== 'yes') {
                output.dim('Reset cancelled.');
                return;
            }
        }

        resetConfig();
        output.success('Configuration reset to defaults.');
    });

function showConfig(json = false, showAll = false): void {
    const config = getConfig();

    if (json) {
        const display = showAll ? config : redactConfig(config);
        output.json(display);
        return;
    }

    output.header('Configuration');
    output.dim(`  File: ${getConfigPath()}`);
    output.newline();

    for (const [key, value] of Object.entries(config)) {
        if (value === undefined || value === null) continue;

        const isSensitive = SENSITIVE_KEYS.has(key);
        const displayValue = isSensitive && !showAll
            ? output.colors.dim(`${'*'.repeat(8)}...${String(value).slice(-4)}`)
            : String(value);

        output.tableRow(key, displayValue);
    }

    output.newline();
    output.dim('  Set values: paean config set <key> <value>');
    output.dim('  Show all:   paean config show --all');
    output.newline();
}

function redactConfig(config: PaeanConfig): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(config)) {
        if (SENSITIVE_KEYS.has(key) && value) {
            result[key] = `${'*'.repeat(8)}...${String(value).slice(-4)}`;
        } else {
            result[key] = value;
        }
    }
    return result;
}
